import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { Chessground } from 'chessground';
import { Api } from 'chessground/api';
import { DrawShape } from 'chessground/draw';
import { Key } from 'chessground/types';
import { Chess } from 'chess.js';
import TryAPI from '../api/TryAPI';
import CorrectionsAPI from '../api/CorrectionsAPI';
import { RepairQueueItem } from '../types/CorrectionModel';
import { Color } from '../types/Color';
import { Platform } from '../types/Platform';
import { toDests } from '../utils/ChessUtils';
import { analytics } from '../utils/analytics';
import { whiteWinPct } from './winChance';
import { ProgressRing } from './TryContainer';

// /blunder/{site}/{username}: one mistake from a named player's recent games, as a position to answer.
// Linked from a reply, so the visitor has typed nothing.
//
// A doorway to /try, not a copy: same /api/Try/start and /api/Try/evaluate, same repair queue
// (/api/Corrections/queue). It only picks one item and takes one guess.
//
// Privacy: the username is in the URL, so it is never an analytics parameter. Events carry the site
// only, and the server rewrites the page path and title for page_view
// (GlobalParameters.TrackingPagePath).

type Phase = 'working' | 'shown' | 'notfound' | 'nogames' | 'nomistake' | 'failed';

// "Their last week of games": older games aren't analysed (about twenty seconds of engine each).
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// Lowest depth counted as a clear mistake: 2 = mistake, miss or blunder.
const CLEAR_MISTAKE_WEIGHT = 2;

// Only this many of the week's games go to the engine, newest first, so the page stays quick.
const MAX_GAMES = 3;

// Mover's win chance this far from level means the game was decided before the move.
const DECIDED_BELOW = 15;
const DECIDED_ABOVE = 85;

function readTarget(): { site: 'chesscom' | 'lichess'; username: string } {
  const data = document.getElementById('app')?.dataset;
  return {
    site: data?.blunderSite === 'lichess' ? 'lichess' : 'chesscom',
    username: data?.blunderUsername ?? '',
  };
}

// From and to squares of a SAN move at a position, for drawing it and for grading a guess by squares.
function squaresOf(fen: string, san: string): [Key, Key] | null {
  try {
    const m = new Chess(fen).move(san);
    return m ? [m.from as Key, m.to as Key] : null;
  } catch {
    return null;
  }
}

// SAN of an engine move given as UCI ("e2e4", "e7e8q") at a position.
function sanOfUci(fen: string, uci: string): string | null {
  try {
    const m = new Chess(fen).move({
      from: uci.slice(0, 2),
      to: uci.slice(2, 4),
      promotion: uci.length > 4 ? uci[4] : undefined,
    });
    return m?.san ?? null;
  } catch {
    return null;
  }
}

// The named player's costliest mistake by win-chance drop. Re-checks the game's White/Black name, so a
// handle the queue failed to match (falling back to White) can't show the opponent's move.
function pickMistake(items: RepairQueueItem[], handle: string): RepairQueueItem | null {
  const me = handle.trim().toLowerCase();
  let best: RepairQueueItem | null = null;
  for (const item of items) {
    const mover = (item.ViewerColor === 'white' ? item.GameWhite : item.GameBlack) ?? '';
    if (mover.toLowerCase() !== me || item.Color !== (item.ViewerColor === 'white' ? Color.White : Color.Black)) {
      continue;
    }
    // Already clearly lost or won before the move.
    const white = whiteWinPct(item.Evaluation);
    const before = item.ViewerColor === 'white' ? white : 100 - white;
    if (item.Evaluation == null || before < DECIDED_BELOW || before > DECIDED_ABOVE) {
      continue;
    }
    // Needs a better move to point at, other than the one played.
    const playedSq = squaresOf(item.Fen, item.PlayedMove);
    if (
      item.Solution.length < 4
      || (playedSq != null && playedSq[0] + playedSq[1] === item.Solution.slice(0, 4))
    ) {
      continue;
    }
    if (best == null || item.PlayedSwing > best.PlayedSwing) {
      best = item;
    }
  }
  return best;
}

export default function BlunderShareContainer(): React.ReactNode {
  const target = useMemo(readTarget, []);
  const platform = target.site === 'lichess' ? Platform.Lichess : Platform.ChessCom;
  const siteLabel = target.site === 'lichess' ? 'Lichess' : 'Chess.com';

  const [phase, setPhase] = useState<Phase>('working');
  const [item, setItem] = useState<RepairQueueItem | null>(null);
  const [handle, setHandle] = useState(target.username);
  const [error, setError] = useState<string | null>(null);
  // Ring checkpoints: games found (0), one per game read, then the pick.
  const [games, setGames] = useState(0);
  const [step, setStep] = useState(0);
  const percent = games === 0 ? 0 : Math.round((step / (games + 1)) * 100);
  const startedRef = useRef(false);

  useEffect(() => {
    // Once per visit, even if React mounts the effect twice.
    if (startedRef.current || target.username === '') {
      return;
    }
    startedRef.current = true;
    analytics.track('blunder_page_start', { site: target.site });

    (async () => {
      try {
        const started = await TryAPI.start(target.username, platform, MAX_GAMES);
        if (started.UserNotFound) {
          setPhase('notfound');
          return;
        }
        if (started.Error != null || started.Games.length === 0) {
          setError(started.Error);
          setPhase('nogames');
          return;
        }
        const since = Date.now() - WEEK_MS;
        const recent = started.Games
          .filter((g) => new Date(g.Date).getTime() >= since)
          .sort((a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime())
          .slice(0, MAX_GAMES);
        if (recent.length === 0) {
          setPhase('nogames');
          return;
        }
        setHandle(started.Handle);
        setGames(recent.length);
        setStep(1);

        for (let i = 0; i < recent.length; i++) {
          await TryAPI.evaluate(recent[i].Id);
          setStep(i + 2);
        }
        const queue = await CorrectionsAPI.getRepairQueue(
          recent.map((g) => g.Id),
          CLEAR_MISTAKE_WEIGHT,
          started.Handle,
        );
        const picked = pickMistake(queue.Items, started.Handle);
        if (picked == null) {
          setPhase('nomistake');
          return;
        }
        setItem(picked);
        setPhase('shown');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Something went wrong. Try again in a moment.');
        setPhase('failed');
      }
    })();
  }, [target, platform]);

  const tryHref =
    `/try?username=${encodeURIComponent(handle)}&platform=${target.site}`;

  return (
    <div className="cm-try">
      {phase === 'working' ? (
        <div className="cm-try-card cm-try-working">
          <ProgressRing percent={percent} />
          <div className="cm-try-working-label">
            {games === 0
              ? `Finding ${handle}’s games from the last 7 days…`
              : step <= games
                ? `Reading game ${step} of ${games} with the engine…`
                : 'Looking for the move that cost most…'}
          </div>
          <p className="cm-try-fineprint">This can take up to a minute while the engine reads them.</p>
        </div>
      ) : null}

      {phase === 'shown' && item != null ? (
        <>
          <MistakeCard item={item} site={target.site} />
          <div className="cm-try-card cm-try-outcome mt-3">
            <h2 className="cm-try-card-title">That was one of them.</h2>
            <p className="cm-try-card-sub">
              There are more in your games. Find them all, and get asked about each one again until
              you stop making it.
            </p>
            <Button
              variant="primary"
              size="lg"
              className="cm-try-cta"
              href={tryHref}
              onClick={() => analytics.track('blunder_page_cta_click', { site: target.site })}
            >
              <FontAwesomeIcon icon={faWrench} className="me-2" />
              Find and repair all your mistakes
            </Button>
          </div>
        </>
      ) : null}

      {phase === 'notfound' ? (
        <Outcome
          title="We couldn’t find that player."
          body={`There is no “${target.username}” on ${siteLabel}. Check the spelling, and that it is the right site.`}
          href="/try"
          cta="Repair your mistakes"
        />
      ) : null}

      {phase === 'nogames' ? (
        <Outcome
          title={`No games from ${handle} in the last 7 days`}
          body={`We didn’t find any games on ${siteLabel} from the last 7 days. The full version goes further back.`}
          href={tryHref}
          cta="Find and repair all your mistakes"
        />
      ) : null}

      {phase === 'nomistake' ? (
        <Outcome
          title={`No clear mistake in ${handle}’s last 7 days of games`}
          body="The engine went through the latest games and didn’t find a move worth asking about. The full version looks across more games."
          href={tryHref}
          cta="Find and repair all your mistakes"
        />
      ) : null}

      {phase === 'failed' ? (
        <Outcome
          title="That didn’t work."
          body="We couldn’t finish looking through the games just now."
          detail={error}
          href="/try"
          cta="Try it on /try"
        />
      ) : null}
    </div>
  );
}

function Outcome(props: {
  title: string;
  body: string;
  detail?: string | null;
  href?: string;
  cta?: string;
}): React.ReactNode {
  return (
    <div className="cm-try-card cm-try-outcome">
      <h2 className="cm-try-card-title">{props.title}</h2>
      <p className="cm-try-card-sub">{props.body}</p>
      {props.detail ? <div className="cm-try-error">{props.detail}</div> : null}
      <Button variant="primary" href={props.href ?? '/try'}>
        {props.cta ?? 'Go to /try'}
      </Button>
    </div>
  );
}

type Answer = {
  // SAN of what the visitor played, or null when they asked to be shown instead.
  guess: string | null;
  guessSquares: [Key, Key] | null;
};

// The position, one guess, then the reveal. No engine numbers: the cost is told as the played move and
// the better one.
function MistakeCard({ item, site }: { item: RepairQueueItem; site: string }): React.ReactNode {
  const boardRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<Api | null>(null);
  const answeredRef = useRef(false);
  const [answer, setAnswer] = useState<Answer | null>(null);

  const played = useMemo(() => squaresOf(item.Fen, item.PlayedMove), [item]);
  const bestSan = useMemo(() => sanOfUci(item.Fen, item.Solution), [item]);
  const best = useMemo<[Key, Key] | null>(
    () => (item.Solution.length >= 4 ? [item.Solution.slice(0, 2) as Key, item.Solution.slice(2, 4) as Key] : null),
    [item],
  );

  const orientation = item.SideToMove === 'black' ? 'black' : 'white';

  const answered = (a: Answer) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    setAnswer(a);
    // Whether they found it; never what they played or whose game it was.
    const found = a.guessSquares != null && best != null
      && a.guessSquares[0] === best[0] && a.guessSquares[1] === best[1];
    analytics.track('blunder_page_answer', {
      site,
      result: a.guess == null ? 'gave_up' : found ? 'found' : 'missed',
    });
  };

  useEffect(() => {
    if (!boardRef.current) return;
    const chess = new Chess(item.Fen);
    const cg = Chessground(boardRef.current, {
      fen: item.Fen,
      orientation,
      turnColor: orientation,
      coordinates: false,
      animation: { enabled: true, duration: 200 },
      movable: {
        free: false,
        color: orientation,
        dests: toDests(chess),
        showDests: true,
        events: {
          after: (orig: Key, dest: Key) => {
            let san: string | null = null;
            try {
              san = new Chess(item.Fen).move({ from: orig, to: dest, promotion: 'q' })?.san ?? null;
            } catch {
              san = null;
            }
            if (san == null) return;
            answered({ guess: san, guessSquares: [orig, dest] });
          },
        },
      },
      draggable: { enabled: true },
      drawable: { enabled: false, visible: true },
    });
    apiRef.current = cg;
    return () => cg.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The reveal: restore the position, then the played move in red and the better one in green.
  useEffect(() => {
    const cg = apiRef.current;
    if (!cg || answer == null) return;
    cg.set({
      fen: item.Fen,
      turnColor: orientation,
      movable: { free: false, color: undefined, dests: new Map() },
      draggable: { enabled: false },
    });
    const shapes: DrawShape[] = [];
    if (played != null) shapes.push({ orig: played[0], dest: played[1], brush: 'red' });
    if (best != null) shapes.push({ orig: best[0], dest: best[1], brush: 'green' });
    cg.setAutoShapes(shapes);
  }, [answer, item, orientation, played, best]);

  const opponent = item.ViewerColor === 'white' ? item.GameBlack : item.GameWhite;
  const colourWord = item.ViewerColor === 'white' ? 'White' : 'Black';

  let verdict: React.ReactNode = null;
  if (answer != null) {
    const sameAs = (sq: [Key, Key] | null) =>
      answer.guessSquares != null && sq != null
      && answer.guessSquares[0] === sq[0] && answer.guessSquares[1] === sq[1];
    if (sameAs(best)) {
      verdict = <>You found the better move — <span className="cm-mono">{bestSan ?? item.Solution}</span>.</>;
    } else if (sameAs(played)) {
      verdict = <>That is the move that was played.</>;
    } else if (answer.guess != null) {
      verdict = <>You played <span className="cm-mono">{answer.guess}</span>.</>;
    }
  }

  return (
    <section className="cm-try-blunder" aria-label="A mistake from this player's own game">
      <p className="cm-try-blunder-context">
        {colourWord} to move. Their own game
        {opponent ? <>, against {opponent}</> : null}, {new Date(item.GameDate).toLocaleDateString()}.
      </p>
      <p className="cm-try-blunder-ask">What would you have played?</p>

      <div className="cm-try-blunder-board" ref={boardRef} />

      {answer == null ? (
        <button
          type="button"
          className="cm-try-blunder-show"
          onClick={() => answered({ guess: null, guessSquares: null })}
        >
          Show me
        </button>
      ) : (
        <div className="cm-try-blunder-reveal">
          {verdict != null ? <p className="cm-try-blunder-verdict">{verdict}</p> : null}
          <p className="cm-try-blunder-why">
            In the game they played <strong className="cm-mono">{item.PlayedMove}</strong> (red).
            {bestSan != null ? (
              <> A better move was <strong className="cm-mono">{bestSan}</strong> (green).</>
            ) : null}
          </p>
        </div>
      )}
    </section>
  );
}
